import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { StyledIcon } from './';
import T from '../../../translate';
import { useRouterPath } from '../../hooks';
import { ArrowRight } from '../../icons';
import { useGlobalStore } from '../../state/globalStore';
import { Margin } from '../../styles/commonStyle';
import {
  BACKGROUND_STYLE,
  ENTITY_TYPE,
  FORM_LAYOUT_OPTIONS,
  KEY_CODES,
  PREVIEW_TYPE,
} from '../../utils/constants';
import { closeGateFromPlayer } from '../../utils/player-controller';
import { PreviewDetails } from '../../utils/types';

export const BackgroundWrapper = ({ children = {}, disableBackground = false }) => {
  const leadCaptureProperties = useGlobalStore((state) => state.leadCapture);
  const entityData = useGlobalStore((state) => state.entity);
  const { background } = leadCaptureProperties;
  const { setPreviewDetails } = useGlobalStore((store) => store);
  const previewRef = useRef<HTMLDivElement>(null);
  const previewDimensions = `${previewRef.current?.offsetWidth}x${previewRef.current?.offsetHeight}`;

  const getBackgroundColor = (hexCode: string, opacity: number) => {
    return `${hexCode}${Math.floor((opacity / 100) * 255).toString(16)}`;
  };

  const getBackgroundUrl = () => {
    if (background.isActive) {
      if (background.useThumbnail) {
        return entityData.thumbnailUrl || '';
      } else {
        return background.url;
      }
    }
    return '';
  };

  const getBackgroundImageStyle = () => {
    let backgroundStyle = {};
    switch (background.style) {
      case BACKGROUND_STYLE.FILL:
        backgroundStyle = {
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
        break;
      case BACKGROUND_STYLE.FIT:
        backgroundStyle = {
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
        };
        break;
      case BACKGROUND_STYLE.TILE:
        backgroundStyle = {
          backgroundSize: '64px',
        };
        break;
    }
    backgroundStyle = {
      backgroundImage: !disableBackground ? `url(${getBackgroundUrl()})` : '',
      ...backgroundStyle,
    };
    return backgroundStyle;
  };

  const getBackgroundColorStyle = () => {
    let backgroundStyle = {
      backgroundColor: !disableBackground
        ? getBackgroundColor(background.hexCode, background.opacity)
        : '',
    };
    return backgroundStyle;
  };

  useEffect(() => {
    const details: PreviewDetails = {
      width: previewRef.current?.offsetWidth,
      height: previewRef.current?.offsetHeight,
      boundingBox: previewRef.current?.getBoundingClientRect(),
    };
    setPreviewDetails(details);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewRef, previewDimensions]);

  return (
    <OuterWrapper ref={previewRef}>
      <ImageOverlay style={getBackgroundImageStyle()} />
      <ColorOverlay style={getBackgroundColorStyle()} />
      <Wrapper>
        <>
          {children}
          <FloatingButton />
        </>
      </Wrapper>
    </OuterWrapper>
  );
};

const FloatingButton = () => {
  const {
    leadCapture: {
      isSkippable,
      layout,
      nonEditableTextStyle: { logIn: logInStyle, skipToVideo },
    },
    previewType,
  } = useGlobalStore((store) => store);
  const { onPreviewChange } = useRouterPath();
  const entityType = useGlobalStore((state) => state.entityType);

  const showSkipToVideo =
    isSkippable &&
    entityType === ENTITY_TYPE.VIDEO &&
    ([PREVIEW_TYPE.FORM, PREVIEW_TYPE.LOGIN_SCREEN] as string[]).includes(previewType);

  const showSignIn =
    layout === FORM_LAYOUT_OPTIONS.SINGLE_STEP_FORM && previewType === PREVIEW_TYPE.FORM;

  return (
    <FloatingButtonWrapper>
      {showSignIn && (
        <FloatingLoginButtonWrapper
          tabIndex={0}
          color={logInStyle.color}
          onClick={() => onPreviewChange(PREVIEW_TYPE.LOGIN_SCREEN)}
          onKeyDown={(e) => e.key === KEY_CODES.ENTER && onPreviewChange(PREVIEW_TYPE.LOGIN_SCREEN)}
        >
          {T.SignIn}
        </FloatingLoginButtonWrapper>
      )}
      {showSkipToVideo && (
        <FloatingSkipButtonWrapper
          tabIndex={0}
          color={skipToVideo.color}
          onClick={() => closeGateFromPlayer()}
          onKeyDown={(e) => {
            e.key === KEY_CODES.ENTER && closeGateFromPlayer();
          }}
        >
          <Margin right={5}>{T.SkipToVideo}</Margin>
          <StyledIcon icon={ArrowRight} color={skipToVideo.color || 'white'} width={18} />
        </FloatingSkipButtonWrapper>
      )}
    </FloatingButtonWrapper>
  );
};

const FloatingLoginButtonWrapper = styled.div<{ color: string }>`
  border-radius: 40px;
  padding: 8px 17px;
  cursor: pointer;
  color: ${({ color }) => color};
`;

const FloatingSkipButtonWrapper = styled.div<{ color: string }>`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 8px 17px;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: ${({ color }) => color};
`;

const FloatingButtonWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  font-weight: 700;
  font-size: 14px;
  display: flex;
  gap: 10px;
`;

const Wrapper = styled.div`
  height: 100%;
  position: relative;
  background-blend-mode: darken;
  overflow-x: auto;
`;

const OuterWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  background: #141414;
  color: white;
`;

const ColorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 0;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 0;
`;
